import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { categories } from '@/mocks/categories';
import { stories } from '@/mocks/stories';

export default function Hero() {
  const [keyword, setKeyword] = useState('');
  const navigate = useNavigate();

  const totalViews = stories.reduce((sum, s) => sum + s.views, 0);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const q = keyword.trim();
    navigate(q ? `/stories?q=${encodeURIComponent(q)}` : '/stories');
  };

  return (
    <section className="relative overflow-hidden border-b border-background-200 bg-background-50">
      <div className="pointer-events-none absolute -right-24 -top-24 h-80 w-80 rounded-full bg-primary-100/70 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-32 left-1/3 h-72 w-72 rounded-full bg-accent-100/60 blur-3xl" />

      <div className="relative mx-auto grid w-full max-w-content grid-cols-1 items-center gap-12 px-4 py-14 md:px-6 md:py-20 lg:grid-cols-2">
        <div>
          <span className="inline-flex items-center gap-2 rounded-full border border-secondary-200 bg-secondary-50 px-3.5 py-1.5 text-xs font-medium text-secondary-900">
            <span className="h-1.5 w-1.5 rounded-full bg-primary-500" />
            今日已更新 3 篇新瓜 · 读者投稿开放中
          </span>

          <h1 className="mt-5 font-heading text-3xl font-bold leading-tight text-foreground-950 md:text-5xl">
            高校吃瓜
            <span className="text-primary-600">，</span>
            围观大学生活的另一面
          </h1>

          <p className="mt-5 max-w-xl text-sm leading-relaxed text-foreground-600 md:text-base">
            从宿舍夜聊到课堂八卦，从社团趣事到食堂奇遇，瓜田笔记每天整理高校吃瓜趣闻与日常，
            把校园里那些真实、好笑又有点温度的故事讲给你听。
          </p>

          <form onSubmit={handleSearch} className="mt-7 flex w-full max-w-lg flex-col gap-2 sm:flex-row">
            <div className="relative flex-1">
              <i className="ri-search-line pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-foreground-400" />
              <input
                type="text"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="搜一搜：食堂、宿舍、社团……"
                className="w-full rounded-md border border-background-300 bg-background-50 py-3 pl-10 pr-3 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
                aria-label="搜索校园吃瓜趣闻"
              />
            </div>
            <button
              type="submit"
              className="whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
            >
              去围观
            </button>
          </form>

          <div className="mt-6 flex flex-wrap items-center gap-2">
            <span className="text-xs text-foreground-500">热门话题</span>
            {categories.slice(0, 4).map((cat) => (
              <button
                key={cat.slug}
                type="button"
                onClick={() => navigate(`/stories?category=${cat.slug}`)}
                className="rounded-full border border-background-200 bg-background-100 px-3 py-1.5 text-xs text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-700"
              >
                {cat.name}
              </button>
            ))}
          </div>

          <dl className="mt-9 grid max-w-lg grid-cols-3 gap-4 border-t border-background-200 pt-6">
            <div>
              <dt className="text-xs text-foreground-500">收录故事</dt>
              <dd className="mt-1 font-heading text-2xl font-bold text-foreground-950">{stories.length * 37}<span className="text-base text-primary-600">+</span></dd>
            </div>
            <div>
              <dt className="text-xs text-foreground-500">累计围观</dt>
              <dd className="mt-1 font-heading text-2xl font-bold text-foreground-950">{Math.round(totalViews / 10000)}<span className="text-base text-primary-600">万</span></dd>
            </div>
            <div>
              <dt className="text-xs text-foreground-500">每个工作日</dt>
              <dd className="mt-1 font-heading text-2xl font-bold text-foreground-950">更新</dd>
            </div>
          </dl>
        </div>

        <div className="relative">
          <div className="relative mx-auto aspect-[4/5] w-full max-w-md overflow-hidden rounded-lg border border-background-200 bg-background-100">
            <img
              src="https://readdy.ai/api/search-image?query=flat%20illustration%20collage%20of%20vibrant%20university%20campus%20life%2C%20students%20chatting%20on%20bikes%2C%20canteen%20trays%2C%20club%20banners%2C%20warm%20terracotta%20orange%20mint%20green%20and%20cream%20palette%2C%20minimal%20editorial%20vector%20style%20with%20subtle%20paper%20texture&width=900&height=1125&seq=gt-hero-main-01&orientation=portrait"
              alt="高校吃瓜日常场景插画：校园里聊天、骑车与社团活动的学生"
              className="h-full w-full object-cover object-top"
            />
          </div>

          <div className="absolute -left-2 bottom-8 flex items-center gap-3 rounded-lg border border-background-200 bg-background-50 px-4 py-3 md:left-0">
            <span className="flex h-10 w-10 items-center justify-center rounded-md bg-accent-100 text-accent-700">
              <i className="ri-flashlight-line text-lg" />
            </span>
            <div className="leading-tight">
              <p className="font-heading text-sm font-bold text-foreground-950">本周最热</p>
              <p className="text-xs text-foreground-500">熄灯后的宿舍电台</p>
            </div>
          </div>

          <div className="animate-float-slow absolute -right-1 top-10 flex items-center gap-2 rounded-full border border-background-200 bg-background-50 px-3.5 py-2 md:right-2">
            <i className="ri-fire-line text-primary-500" />
            <span className="text-xs font-medium text-foreground-800">今日最热瓜</span>
          </div>
        </div>
      </div>
    </section>
  );
}